import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { FormStateValues } from '@components/form';
import { useToaster } from '@components/toaster';

import { unhandledRequest } from 'api/request';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';
import { FormConfig } from 'types/forms';

export type Template = {
  id: number;
  title: string;
};

export type Cosigner = {
  id: number;
  name: string;
  email: string;
  ssn: string;
  phone: string;
};

export type EditCosigner = Omit<Cosigner, 'id'>;

export type SubmitDocumentForm = Record<string, any> & { cosigners: string[]; save?: boolean };

type Signer = {
  status: 'pending' | 'signed' | 'declined';
  name: string;
};

type Contract = {
  id: string;
  data: Record<string, any>;
  templateTitle: string;
  senderName: string;
  dateSent: string;
  signers: Signer[];
  url: string;
  inProgress?: boolean;
  templateId?: string;
};

type FormResponse = {
  name: string;
  description: string;
  title: string;
  cosigners: Cosigner[];
  templates: Template[];
  contracts: Contract[];
};

export type DokobitTemplateResponse = FormConfig & {
  cosigners?: {
    value: string;
    label: string;
  }[];
};

export async function dokobitGetToken() {
  const response = await unhandledRequest.get<{ ok: boolean }>({
    url: `/integrations/dokobit/settings/token/`,
  });
  return response.data;
}

export async function dokobitSetToken(token: string) {
  const response = await unhandledRequest.post<{ ok: boolean }>({
    url: `/integrations/dokobit/settings/token/`,
    data: { token },
  });
  return response.data;
}

export async function dokobitGetCosigners() {
  const response = await unhandledRequest.get<Cosigner[]>({
    url: `/integrations/dokobit/settings/cosigners/`,
  });
  return response.data;
}

export async function dokobitAddCosigner(data: EditCosigner) {
  const response = await unhandledRequest.post({
    url: `/integrations/dokobit/settings/cosigners/`,
    data: { ...data },
  });
  return response.data;
}

export async function dokobitEditCosigner(cosignerId: string, data: Omit<Cosigner, 'id'>) {
  const response = await unhandledRequest.patch({
    url: `/integrations/dokobit/settings/cosigners/${cosignerId}/`,
    data: { ...data },
  });
  return response.data;
}

export async function dokobitRemoveCosigner(cosignerId: string) {
  const response = await unhandledRequest.delete({
    url: `/integrations/dokobit/settings/cosigners/${cosignerId}/`,
  });
  return response.data;
}

export async function dokobitForm(applicationID: string) {
  const response = await unhandledRequest.get<FormResponse>({
    url: `/integrations/dokobit/${applicationID}/form/`,
  });
  return response.data;
}

export async function dokobitTemplate(templateID: string, applicationID: string) {
  const response = await unhandledRequest.get<DokobitTemplateResponse>({
    url: `/integrations/dokobit/templates/${templateID}/${applicationID}/`,
  });
  return response.data;
}

export async function deleteSavedForms(templateID: string, applicationID: string) {
  const response = await unhandledRequest.delete({
    url: `/integrations/dokobit/templates/${templateID}/${applicationID}/`,
  });
  return response.data;
}

type PreviewResponse = {
  url: string;
};

export async function dokobitPreviewDocument(
  applicationID: string,
  templateID: string,
  data: FormStateValues
) {
  const response = await unhandledRequest.post<PreviewResponse>({
    url: `/integrations/dokobit/${applicationID}/form/${templateID}/preview/`,
    data,
  });
  return response.data;
}

export type SubmitSuccessResponse = {
  ok: boolean;
};

type SubmitErrorResponse = {
  ok: boolean;
  errors: string[];
};

export type SubmitError = AxiosError<SubmitErrorResponse>;

type SubmitResponse = SubmitSuccessResponse | SubmitErrorResponse;

export type DokobitForm = Record<string, any> & { cosigners: string[] };

export async function dokobitSubmitDocument(
  applicationID: string,
  templateID: string,
  data: DokobitForm
) {
  const response = await unhandledRequest.post<SubmitResponse>({
    url: `/integrations/dokobit/${applicationID}/form/${templateID}/sign/`,
    data,
  });
  return response.data;
}

export async function dokobitDeleteDocument(contractId: string) {
  const response = await unhandledRequest.post({
    url: `/integrations/dokobit/${contractId}/delete/`,
  });
  return response.data;
}

export const usePreviewDocument = (applicationId: string, templateId: string) => {
  const { showFullscreenModal } = useModal();

  return useMutation(
    (data: FormStateValues) => dokobitPreviewDocument(applicationId, templateId, data),
    {
      mutationKey: ['dokobit-preview-document'],
      onSuccess: (data) => {
        showFullscreenModal('CUSTOM_FILE_PREVIEW', { filePath: data.url });
      },
    }
  );
};

export const useSubmitDokobitDocument = (applicationId: string, templateId: string) => {
  const { t } = useStore();
  const { showSuccess } = useToaster();
  const client = useQueryClient();

  return useMutation<SubmitSuccessResponse, SubmitError, SubmitDocumentForm & { saved?: boolean }>({
    mutationKey: ['dokobit-submit-document'],
    mutationFn: (data) => {
      return dokobitSubmitDocument(applicationId, templateId, data);
    },
    onSuccess: (data, variables) => {
      if (variables.save) {
        showSuccess(t('integrations.dokobit.onboarding.form.saved'));
      } else showSuccess(t('integrations.dokobit.onboarding.form.success'));
      client.invalidateQueries(['dokobit-template', templateId]);
      client.resetQueries(['dokobit-onboarding-form', applicationId]);
      client.invalidateQueries(['application-onboarding-load', applicationId]);
    },
  });
};

export const useDokobitTemplate = (applicationId: string, templateId: string) => {
  return useQuery({
    queryKey: ['dokobit-template', templateId, applicationId],
    queryFn: () => dokobitTemplate(templateId, applicationId),
  });
};

export const useDokobitForm = (applicationId: string) => {
  return useQuery({
    queryKey: ['dokobit-onboarding-form', applicationId],
    queryFn: () => dokobitForm(applicationId),
  });
};

export const useDeleteDokobitDocument = (
  applicationId: string,
  options?: UseMutationOptions<unknown, unknown, string>
) => {
  const { t } = useStore();
  const { showSuccess } = useToaster();
  const client = useQueryClient();

  return useMutation({
    mutationFn: dokobitDeleteDocument,
    ...options,
    onSuccess(...data) {
      showSuccess(t('integrations.dokobit.onboarding.contract.delete.notification'));
      client.resetQueries(['dokobit-onboarding-form', applicationId]);
      client.invalidateQueries(['application-onboarding-load', applicationId]);
      options?.onSuccess?.(...data);
    },
  });
};

export const useAddDokobitCosigner = (
  options?: UseMutationOptions<unknown, unknown, EditCosigner>
) => {
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const client = useQueryClient();

  return useMutation({
    mutationFn: dokobitAddCosigner,
    ...options,
    onSuccess: (...data) => {
      showSuccess(t('integrations.dokobit.settings.cosigners.add.success'));
      client.resetQueries(['dokobit-cosigners']);
      options?.onSuccess?.(...data);
    },
    onError: (error, ...rest) => {
      const errorMessage = Array.isArray(error) ? error[0] : String(error);
      showError(errorMessage);
      options?.onError?.(error, ...rest);
    },
  });
};

export const useEditDokobitCosigner = (
  id: number,
  options?: UseMutationOptions<unknown, unknown, EditCosigner>
) => {
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (cosigner) => dokobitEditCosigner(String(id), cosigner),
    ...options,
    onSuccess: (...data) => {
      showSuccess(t('integrations.dokobit.settings.cosigners.edit.success'));
      client.resetQueries(['dokobit-cosigners']);
      options?.onSuccess?.(...data);
    },
    onError: (error, ...rest) => {
      const errorMessage = Array.isArray(error) ? error[0] : String(error);
      showError(errorMessage);
      options?.onError?.(error, ...rest);
    },
  });
};

export const useDeleteDokobitForm = (applicationId: string, templateId: string) => {
  const { t } = useStore();
  const { showSuccess } = useToaster();
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => deleteSavedForms(templateId, applicationId),
    onSuccess: () => {
      showSuccess(t('integrations.dokobit.onboarding.form.deleted'));
      client.resetQueries(['dokobit-onboarding-form', applicationId]);
      client.invalidateQueries(['dokobit-template', templateId]);
    },
  });
};

export type DokobitSignatureOptions = 'first_top' | 'first_bottom' | 'last_top' | 'last_bottom';

export type DokobitSignatureLocation = {
  signatureLocation: DokobitSignatureOptions;
};

export const useDokobitSignatureLocation = () => {
  return useQuery({
    queryKey: ['dokobit-signature-location'],
    queryFn: dokobitGetSignatureLocation,
  });
};

export async function dokobitGetSignatureLocation() {
  const response = await unhandledRequest.get<DokobitSignatureLocation>({
    url: `/integrations/dokobit/settings/signaturelocation/`,
  });
  return response.data;
}

export function updateDokobitSignatureLocation(data: DokobitSignatureLocation) {
  return unhandledRequest.patch({
    url: '/integrations/dokobit/settings/signaturelocation/',
    data,
  });
}

export const useUpdateDokobitSignatureLocation = () => {
  const { t } = useStore();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToaster();
  return useMutation(updateDokobitSignatureLocation, {
    onSuccess: () => {
      showSuccess(t('integrations.dokobit.signaturelocation.success'));
      return queryClient.invalidateQueries(['dokobit-signature-location']);
    },
    onError: () => showError(t('integrations.dokobit.signaturelocation.error')),
  });
};
